import { DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { useAppTFunction } from '@retail/app/i18n';
import { withDialogSuspenseErrorBoundary } from '@retail/components';
import { ReplaceItemDialogContent, ReplaceItemFunction } from '@retail/my-assortment/components';
import { useFetchAssortmentTags } from '@retail/my-assortment/data-access';
import { AssortmentTag, CentralAssortmentItem } from '@retail/my-assortment/types';
import { MgSupplierItemSearchResultDTO } from '@retail/products/types';
import { getActiveAssortmentTags } from '@retail/products/utils';
import { getISO_8601Date } from '@retail/utils';
import { Dialog, Stepper } from '@shared/components';
import { useCallback, useMemo, useState } from 'react';
import { SelectReplacementItem } from './SelectReplacementItem';
import { SelectedReplacementItem } from './SelectedReplacementItem';
import { useReplaceAssortmentItem } from './useReplaceAssortmentItem';

interface Props {
  open: boolean;
  assortmentId: number;
  itemToReplace: CentralAssortmentItem;
  onClose: () => void;
}

function ReplaceAssortmentItem({ open, assortmentId, itemToReplace, onClose }: Props) {
  const t = useAppTFunction();

  const steps = useMemo(
    () => [t('myAssortment.actions.selectReplacementItem'), t('myAssortment.actions.editItem')],
    [t]
  );
  const [activeStep, setActiveStep] = useState(0);

  const [selectedItem, setSelectedItem] = useState<MgSupplierItemSearchResultDTO | undefined>();

  const { data: assortmentTagDtos = [] } = useFetchAssortmentTags({
    assortmentIds: [assortmentId],
  });
  const assortmentTags = useMemo(
    () => assortmentTagDtos.map(AssortmentTag.fromDTO),
    [assortmentTagDtos]
  );

  // The replacement takes over the sub-assortments the replaced item is in.
  const defaultTagIds = useMemo(
    () => getActiveAssortmentTags(itemToReplace.assortmentTags).map(({ tagId }) => tagId),
    [itemToReplace.assortmentTags]
  );

  const { replaceItem, isSaving } = useReplaceAssortmentItem({ onItemReplaced: onClose });

  const handleReplaceItem: ReplaceItemFunction = useCallback(
    ({ replacementDate, priority, tagIds }) => {
      if (!selectedItem) return;

      const validFrom = getISO_8601Date(replacementDate);

      replaceItem(
        {
          mgSupplierId: selectedItem.mgSupplierId,
          mgItemNumber: selectedItem.mgItemNumber,
          priority,
          validFrom,
          assortmentTags: tagIds.map((tagId) => ({
            tagId,
            tagName: assortmentTags.find(({ id }) => id === tagId)?.name,
            validFrom,
          })),
        },
        itemToReplace.mgSupplierItemId
      );
    },
    [assortmentTags, itemToReplace.mgSupplierItemId, replaceItem, selectedItem]
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      {/* Stacked rather than side by side, as the stepper does not fit next to the title here */}
      <DialogTitle sx={{ flexDirection: 'column', gap: 2 }}>
        <Stack>
          {t('myAssortment.actions.replaceItem')}
          <Typography variant="body2" color="text.secondary">
            {t('myAssortment.actions.replacesItem', {
              itemNumber: itemToReplace.mgItemNumber,
              itemName: itemToReplace.primaryText,
            })}
          </Typography>
        </Stack>
        <Stepper steps={steps} activeStep={activeStep} labelOrientation="horizontal" />
      </DialogTitle>

      {activeStep === 0 && (
        <SelectReplacementItem
          selectedItem={selectedItem}
          selectItem={setSelectedItem}
          clearSelectedItem={() => setSelectedItem(undefined)}
          onClose={onClose}
          onContinue={() => setActiveStep(1)}
        />
      )}
      {activeStep === 1 && selectedItem && (
        <>
          <DialogContent sx={{ pb: 0 }}>
            <SelectedReplacementItem item={selectedItem} />
          </DialogContent>
          <ReplaceItemDialogContent
            assortmentTags={assortmentTags}
            defaultTagIds={defaultTagIds}
            isSaving={isSaving}
            handleReplaceItem={handleReplaceItem}
            onClose={onClose}
            goBack={() => setActiveStep(0)}
          />
        </>
      )}
    </Dialog>
  );
}

export default withDialogSuspenseErrorBoundary(ReplaceAssortmentItem);
